import React, { useState } from "react";
import { RefreshCw, ShieldCheck, ShieldAlert, Copy, Terminal } from "lucide-react";
import { dataApi, formatApiError } from "../api/client";

/**
 * DedupStateView — inspector del guard de deduplicación de asignaciones.
 * Sólo visible para super_admin (LauncherView filtra la ruta).
 *
 * Muestra:
 *  - build_marker: timestamp de build → sirve para verificar que un redeploy
 *    de producción realmente tomó los últimos cambios de backend.
 *  - accept_timeout_seconds: valor efectivo (esperado 180 = 3 min).
 *  - has_cancel_reassign_timer_fn: true si Iteración 54 está aplicada.
 *  - assignment_notified: dict de rides con guard activo.
 *  - reassign_tasks: timers de auto-reasignación pendientes.
 */
export default function DedupStateView() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [lastFetch, setLastFetch] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await dataApi.dedupState();
      setState(data);
      setLastFetch(new Date());
    } catch (e) {
      setErr(formatApiError(e));
      setState(null);
    } finally {
      setLoading(false);
    }
  };

  const copyJson = () => {
    if (!state) return;
    try {
      navigator.clipboard.writeText(JSON.stringify(state, null, 2));
    } catch (e) {
      // clipboard puede estar bloqueado en contextos no-https; ignoramos.
    }
  };

  // Semáforo de salud
  const health = state
    ? {
        ok:
          state.accept_timeout_seconds === 180 &&
          state.has_cancel_reassign_timer_fn === true &&
          state.notify_driver_dedup_active === true,
        reasons: [
          state.accept_timeout_seconds !== 180 &&
            `accept_timeout_seconds=${state.accept_timeout_seconds} (esperado 180)`,
          state.has_cancel_reassign_timer_fn !== true && "función _cancel_reassign_timer AUSENTE (Iter54 no aplicada)",
          state.notify_driver_dedup_active !== true && "dedup no activo",
        ].filter(Boolean),
      }
    : null;

  return (
    <div data-testid="dedup-state-view" className="max-w-[1200px] mx-auto p-6 space-y-5">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted flex items-center gap-2">
            <Terminal className="w-3 h-3" /> Backend Diagnóstico
          </div>
          <h1 className="font-display text-2xl font-semibold text-mdd-text tracking-tight">
            Guard de deduplicación
          </h1>
          <p className="text-sm text-mdd-muted mt-1 max-w-2xl">
            Estado en vivo del dedup de asignaciones. Sirve para confirmar que
            el fix del banner-duplicado (Iter54) está desplegado en el pod actual.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {state && (
            <button
              data-testid="dedup-copy-btn"
              onClick={copyJson}
              className="mdd-btn mdd-btn-ghost flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Copiar JSON
            </button>
          )}
          <button
            data-testid="dedup-refresh-btn"
            onClick={load}
            disabled={loading}
            className="mdd-btn mdd-btn-primary flex items-center gap-2 disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Consultando..." : state ? "Recargar" : "Consultar ahora"}
          </button>
        </div>
      </header>

      {err && (
        <div
          data-testid="dedup-error"
          className="mdd-card p-4 border-l-4 border-red-500 bg-red-500/5 text-red-300 text-sm"
        >
          {err}
        </div>
      )}

      {!state && !err && (
        <div className="mdd-card p-8 text-center text-mdd-muted">
          Presiona <span className="text-mdd-text font-medium">Consultar ahora</span> para
          obtener el estado del dedup del backend.
        </div>
      )}

      {state && (
        <>
          {/* Semáforo de salud */}
          <div
            data-testid="dedup-health"
            className={`mdd-card p-4 border-l-4 flex items-start gap-3 ${
              health.ok ? "border-emerald-500 bg-emerald-500/5" : "border-yellow-500 bg-yellow-500/5"
            }`}
          >
            {health.ok ? (
              <ShieldCheck className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
            ) : (
              <ShieldAlert className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <div className="font-medium text-mdd-text">
                {health.ok
                  ? "Fix de Iteración 54 desplegado correctamente"
                  : "Se detectaron inconsistencias — el pod no tiene el fix esperado"}
              </div>
              {!health.ok && health.reasons.length > 0 && (
                <ul className="text-sm text-mdd-muted mt-1 list-disc list-inside">
                  {health.reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
              {lastFetch && (
                <div className="text-xs text-mdd-muted mt-2 font-mono">
                  Último snapshot: {lastFetch.toLocaleString("es-PE")}
                </div>
              )}
            </div>
          </div>

          {/* Tarjetas de métricas */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard label="Build marker" value={state.build_marker} mono />
            <MetricCard
              label="Accept timeout"
              value={`${state.accept_timeout_seconds}s`}
              ok={state.accept_timeout_seconds === 180}
            />
            <MetricCard
              label="Guard activo"
              value={Object.keys(state.assignment_notified || {}).length}
              suffix="rides"
            />
            <MetricCard
              label="Timers vivos"
              value={(state.reassign_tasks || []).filter((t) => !t.done).length}
              suffix="tasks"
            />
          </div>

          {/* Tabla de rides con guard */}
          <div className="mdd-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-mdd-text">
                Rides con guard vigente ({Object.keys(state.assignment_notified || {}).length})
              </h3>
            </div>
            {Object.keys(state.assignment_notified || {}).length === 0 ? (
              <div className="text-sm text-mdd-muted italic">
                No hay guards activos en este momento (todos los rides completaron o no hay asignaciones abiertas).
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-mdd-muted border-b border-white/10">
                    <tr>
                      <th className="text-left py-2 px-2">Ride ID</th>
                      <th className="text-left py-2 px-2">Driver notificado</th>
                      <th className="text-left py-2 px-2">Timer</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-xs">
                    {Object.entries(state.assignment_notified).map(([rid, did]) => {
                      const timer = (state.reassign_tasks || []).find((t) => t.ride_id === rid);
                      return (
                        <tr key={rid} data-testid={`dedup-row-${rid}`} className="border-b border-white/5">
                          <td className="py-2 px-2 text-mdd-text">{rid}</td>
                          <td className="py-2 px-2 text-mdd-muted">{did}</td>
                          <td className="py-2 px-2">
                            {timer ? (
                              timer.cancelled ? (
                                <span className="text-yellow-400">cancelled</span>
                              ) : timer.done ? (
                                <span className="text-mdd-muted">done</span>
                              ) : (
                                <span className="text-emerald-400">activo (3 min)</span>
                              )
                            ) : (
                              <span className="text-mdd-muted">— (aceptada)</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* JSON crudo para copia/pega a soporte */}
          <details className="mdd-card p-4">
            <summary className="cursor-pointer text-sm text-mdd-muted font-mono">
              Ver JSON crudo (para pegar en tickets)
            </summary>
            <pre
              data-testid="dedup-raw-json"
              className="mt-3 text-xs font-mono bg-black/40 border border-white/5 rounded-lg p-3 overflow-x-auto text-mdd-text"
            >
              {JSON.stringify(state, null, 2)}
            </pre>
          </details>
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value, suffix, mono, ok }) {
  return (
    <div className="mdd-card p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted">{label}</div>
      <div
        className={`mt-1 font-semibold text-mdd-text ${mono ? "font-mono text-xs break-all" : "text-2xl"} ${
          ok === false ? "text-yellow-400" : ok === true ? "text-emerald-400" : ""
        }`}
      >
        {value}
        {suffix && <span className="text-xs text-mdd-muted ml-1 font-normal">{suffix}</span>}
      </div>
    </div>
  );
}
